import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  ErrorInfo,
} from "react";
import { Outlet, useMatch } from "react-router";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import { createLogger } from "debug/DebugingLogger";
import { axiosInstance, storageServiceApi } from "contexts/axiosApiInstance";
import { useAuth } from "contexts/AuthProvider";
import { noteSessionClientId } from "@/lib/session/sessionWatcher";
import { useRememberShellPage } from "@/lib/navigation/canvasHome";
import { SHELL_PAGES } from "@/lib/navigation/pages";
import { Sidebar } from "./utils/Sidebar";
import { OrgDeletionBanner } from "./organizations/OrgDeletionBanner";
import { PlanBanner } from "./organizations/PlanBanner";
import { BannerSlot } from "./organizations/BannerSlot";
import { TermsBanner } from "@/components/authentication/consent/TermsBanner";
import { SupportPanel } from "./support/SupportPanel";
import { ActionChip, VersionChip } from "./utils/common/VersionChip";
import { openGuide } from "@/components/guide/guides";
import { useMainStateStore } from "./stores/mainStateStore";
import { useOrgContextStore } from "./stores/orgContextStore";
import { useTokensStore } from "./stores/tokensStore";
import { useAppInitializationStore } from "./stores/appInitializationStore";
import { useShallow } from "zustand/react/shallow";
import { RailOpenContext } from "@/components/main/utils/railOpen";
import { useOnlineStatus } from "@/components/hooks/useOnlineStatus";
const log = createLogger("MainLayout");

class DebugErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    log.error(
      "CAUGHT: %s COMPONENT_STACK: %s",
      error.message,
      errorInfo.componentStack,
    );
  }
  render() {
    if (this.state.hasError) {
      setTimeout(() => this.setState({ hasError: false }), 100);
      return null;
    }
    return this.props.children;
  }
}

export const MainLayout = () => {
  useRememberShellPage();
  // The filter belongs to one Overview visit; any other way in starts unfiltered.
  const onOverview = useMatch(SHELL_PAGES.Overview.path) !== null;
  const setSelectedSpace = useOrgContextStore((s) => s.setSelectedSpace);
  useEffect(() => {
    if (!onOverview) setSelectedSpace(null);
  }, [onOverview, setSelectedSpace]);

  const { authenticated } = useAuth();
  const { fetchModelsList } = useMainStateStore(
    useShallow((state) => ({ fetchModelsList: state.fetchModelsList })),
  );
  const { fetchTokensList } = useTokensStore(
    useShallow((state) => ({ fetchTokensList: state.fetchTokensList })),
  );
  const { modelsInitialized, tokensInitialized, isAppInitialized } =
    useAppInitializationStore(
      useShallow((state) => ({
        modelsInitialized: state.modelsInitialized,
        tokensInitialized: state.tokensInitialized,
        isAppInitialized: state.isAppInitialized(),
      })),
    );

  const isOnline = useOnlineStatus();
  const [bannerState, setBannerState] = useState<
    "none" | "offline" | "restored"
  >("none");
  const prevOnlineRef = useRef(isOnline);

  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;

    if (!isOnline) {
      setBannerState("offline");
    } else if (!wasOnline) {
      setBannerState("restored");
      const timer = setTimeout(() => setBannerState("none"), 1500);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => JSON.parse(localStorage.getItem("sidebarOpen") || "false") as boolean,
  );
  const leftSidebarRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const initializationStartedRef = useRef(false);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarOpen", JSON.stringify(newState));
      return newState;
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && isSidebarOpen) {
        setIsSidebarOpen(false);
        localStorage.setItem("sidebarOpen", "false");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen]);

  // On :root because portaled dialogs center over the content column, not the
  // viewport; cleared on unmount so the canvas doesn't inherit it.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--main-sidebar-width",
      isSidebarOpen
        ? "var(--sidebar-width-open)"
        : "var(--sidebar-width-closed)",
    );
    return () => {
      document.documentElement.style.removeProperty("--main-sidebar-width");
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!navigator.onLine) return;
    axiosInstance
      .get("/action/users/me")
      .then((res) => {
        if (res.data) {
          log.info("User ID found in the response %s", res.data.user_id);
          localStorage.setItem("clientID", res.data.user_id);
          noteSessionClientId(res.data.user_id);
          localStorage.setItem("email", res.data.email);
          storageServiceApi
            .post("init_storage_profile")
            .then(() => log.info("Storage profile initialized successfully"))
            .catch((error) =>
              log.error("Failed to initialize storage profile: %o", error),
            );
        } else {
          log.error("User ID not found in the response");
        }
      })
      .catch((error) => log.error("Error fetching user profile: %o", error));
  }, []);

  useEffect(() => {
    if (!authenticated || !isOnline) return;
    if (modelsInitialized && tokensInitialized) return;
    if (initializationStartedRef.current && !isAppInitialized) {
      // The last attempt failed offline; let it retry.
      initializationStartedRef.current = false;
    }
    if (initializationStartedRef.current) return;
    initializationStartedRef.current = true;

    const initPromises: Promise<unknown>[] = [];
    if (!modelsInitialized) {
      initPromises.push(
        fetchModelsList({ showLoading: false }).catch((error) =>
          log.error("Failed to initialize models: %o", error),
        ),
      );
    }
    if (!tokensInitialized) {
      initPromises.push(
        fetchTokensList({ showLoading: false }).catch((error) =>
          log.error("Failed to initialize tokens: %o", error),
        ),
      );
    }
    Promise.all(initPromises).catch((error) =>
      log.error("Error during app initialization: %o", error),
    );
  }, [
    authenticated,
    isOnline,
    isAppInitialized,
    modelsInitialized,
    tokensInitialized,
    fetchModelsList,
    fetchTokensList,
  ]);

  return (
    <div className="flex w-full h-screen overflow-hidden relative">
      <div
        className="h-full shrink-0 overflow-hidden"
        style={{
          width: isSidebarOpen ? "240px" : "48px",
          transition:
            "width var(--sidebar-collapse-duration) var(--sidebar-collapse-ease)",
        }}
        ref={leftSidebarRef}
      >
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      </div>
      <RailOpenContext.Provider value={isSidebarOpen}>
        <div
          data-main-pane
          className="flex flex-col flex-1 min-w-0 min-h-0 bg-bg @container"
        >
          <BannerSlot>
            <OrgDeletionBanner />
            <PlanBanner />
            <TermsBanner />
            {bannerState === "offline" && (
              <div className="offline-banner" role="status" aria-live="polite">
                <div className="banner-dot" />
                <span>You're offline</span>
              </div>
            )}
            {bannerState === "restored" && (
              <div
                className="offline-banner restored"
                role="status"
                aria-live="polite"
              >
                <div className="banner-check" />
                <span>Connected</span>
              </div>
            )}
          </BannerSlot>
          <DebugErrorBoundary>
            <Outlet
              context={{
                headerRef,
                sidebarRef: leftSidebarRef,
                isMobile: window.innerWidth < 1024,
                toggleSidebar,
              }}
            />
          </DebugErrorBoundary>
        </div>
      </RailOpenContext.Provider>
      <div className="absolute bottom-4 right-4 z-10 flex items-center">
        {onOverview && (
          <ActionChip
            className="tour-start"
            icon={<HelpOutlineIcon className="icon-xs" />}
            label="Take a tour"
            onClick={() => openGuide("overview")}
          />
        )}
        <VersionChip />
      </div>
      <SupportPanel />
    </div>
  );
};
